import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import RequestDetailsDialog from "@/components/RequestDetailsDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  FileText,
  User,
  Inbox,
  ChevronRight,
  MoreHorizontal,
  CheckCircle2,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import {
  getRegularizationRequests,
  updateRegularizationRequests,
} from "@/services/attendanceService";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
  PaginationPageSize,
} from "@/components/ui/pagination";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Regularization = () => {
  const [activeTab, setActiveTab] = useState("late-arrival");
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [regularizationRequests, setRegularizationRequests] = useState([]);
  const [missedPunchInRequests, setMissedPunchInRequests] = useState([]);
  const [earlyPunchOutRequests, setEarlyPunchOutRequests] = useState([]);
  const [showRejectionDialog, setShowRejectionDialog] = useState(false);
  const [selectedRegularization, setSelectedRegularization] = useState(null);
  const [regComment, setRegComment] = useState("");
  const [dialogAction, setDialogAction] = useState(null);
  const { can } = usePermission();
  const { canEdit } = createCanHelpers(can);
  const canEditRegularization = canEdit("regularization_requests");
  const [page, setPage] = useState(1);

  const [pageSize, setPageSize] = useState(5);
  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setPage(1); // reset to first page when page size changes
  };
  const isLateArrivalTab = activeTab === "late-arrival";
  const isMissedPunchTab = activeTab === "missed-punch-in";
  const isEarlyPunchTab = activeTab === "early-punch-outs";
  // All three request types are now status-driven (employee applies → admin
  // reviews by status), so the status filter/counters apply to all of them.
  const tabHasStatus = isLateArrivalTab || isMissedPunchTab || isEarlyPunchTab;
  const activeRequests = isLateArrivalTab
    ? regularizationRequests
    : isMissedPunchTab
      ? missedPunchInRequests
      : earlyPunchOutRequests;
  const filteredRequests =
    !tabHasStatus || statusFilter === "all"
      ? activeRequests
      : activeRequests.filter((req) => req.status === statusFilter);
  // Stat cards summarise all status-based requests (late arrival + missed
  // punch-in + early punch-out).
  const statusRequests = [
    ...regularizationRequests,
    ...missedPunchInRequests,
    ...earlyPunchOutRequests,
  ];
  const totalItems = filteredRequests.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    setPage(1);
  }, [statusFilter, activeTab]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagedRequests = React.useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRequests.slice(start, start + pageSize);
  }, [filteredRequests, page, pageSize]);

  const buildPages = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages = new Set([1, total, current, current - 1, current + 1]);
    const cleaned = [...pages]
      .filter((p) => p >= 1 && p <= total)
      .sort((a, b) => a - b);

    const result = [];
    for (let i = 0; i < cleaned.length; i++) {
      result.push(cleaned[i]);
      if (i < cleaned.length - 1 && cleaned[i + 1] - cleaned[i] > 1) {
        result.push("...");
      }
    }
    return result;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return (
          <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/10 gap-1">
            <CheckCircle className="h-3 w-3" />
            Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20 hover:bg-red-500/10 gap-1">
            <XCircle className="h-3 w-3" />
            Rejected
          </Badge>
        );
      case "pending":
        return (
          <Badge className="bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20 hover:bg-yellow-500/10 gap-1">
            <Clock className="h-3 w-3" />
            Pending
          </Badge>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    fetchRegularization();
  }, []);

  const fetchRegularization = async () => {
    setLoading(true);
    try {
      const response = await getRegularizationRequests();
      setRegularizationRequests(response?.lateArrivalRequests || []);
      setMissedPunchInRequests(response?.missedPunchInRequests || []);
      setEarlyPunchOutRequests(response?.earlyPunchOutRequests || []);
    } catch (error) {
      toast.error("failed to get Regularization requests");
    } finally {
      setLoading(false);
    }
  };

  const upsertRegularizationRequest = async (data, status) => {
    try {
      await updateRegularizationRequests({
        regularizationId: data._id,
        status,
        comment: regComment,
      });
      toast.success("Successfully update Regularization request");
      setShowRejectionDialog(false);
      setSelectedRegularization(null);
      fetchRegularization();
    } catch (error) {
      toast.error("failed to update Regularization request");
    }
  };

  const isEarlyPunchOutRequest =
    selectedRegularization?.event_type === "early_punch_out";
  const isPendingRequest = selectedRegularization?.status === "pending";
  // Early punch-out is now employee-applied and reviewed by status, like the
  // other two types.
  const canReviewSelectedRequest = isPendingRequest;

  const renderRequestList = (requests, emptyTitle, emptyDescription, type) => {
    if (loading) {
      return Array.from({ length: 4 }).map((_, i) => (
        <div key={`reg-skeleton-${type}-${i}`} className="p-4 border border-border rounded-xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      ));
    }

    if (requests?.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-4 border border-dashed border-border rounded-xl bg-muted/20 text-center animate-in fade-in zoom-in-95 duration-200">
          <div className="bg-background p-4 rounded-full shadow-sm mb-3">
            <Inbox className="h-8 w-8 text-muted-foreground/50" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">
            {emptyTitle}
          </h3>
          <p className="text-sm text-muted-foreground max-w-sm mt-1">
            {emptyDescription}
          </p>
        </div>
      );
    }

    return requests.map((request) => {
      const isMissed = type === "missed-punch-in";
      const isLate = type === "late-arrival";
      const isEarly = type === "early-punch-out";
      // All three types are reviewed by regularization status now.
      const showStatus = isLate || isMissed || isEarly;
      const canReviewRequest = showStatus
        ? request.status === "pending"
        : request.impacts_half_day === "pending_review";

      // The two data columns differ per request type.
      const colA = isMissed
        ? { label: "Punch In", value: request.actual_check_in }
        : isLate
          ? { label: "Shift In", value: request.expected_check_in }
          : { label: "Shift Out", value: request.expected_check_out };
      const colB = isMissed
        ? { label: "Punch Out", value: request.actual_check_out }
        : isLate
          ? { label: "Actual Check In Time", value: request.actual_check_in }
          : { label: "Actual Check Out Time", value: request.actual_check_out };

      return (
        <div
          key={request._id || request.id}
          onClick={() => {
            setRegComment("");
            setDialogAction(null);
            setShowRejectionDialog(true);
            setSelectedRegularization(request);
          }}
          className="p-4 border border-border rounded-xl hover:bg-muted/50 transition-colors cursor-pointer"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4 w-[220px]">
              <div className="h-10 w-10 rounded-full flex items-center justify-center text-slate-400 border border-slate-700">
                <User size={18} />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  {request.employee_name}
                </h4>
                <p className="text-xs text-slate-500">{request.employee_id}</p>
              </div>
            </div>

            <div className="flex-1 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 text-sm">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground font-medium">
                    Date
                  </span>
                  <span className="text-foreground font-medium truncate">
                    {new Date(request.date).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </div>

                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground font-medium">
                    {colA.label}
                  </span>
                  <span className="text-foreground font-medium truncate">
                    {colA.value || "N/A"}
                  </span>
                </div>

                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground font-medium">
                    {colB.label}
                  </span>
                  <span className="text-foreground font-medium truncate">
                    {colB.value || "N/A"}
                  </span>
                </div>

                {showStatus && (
                  <div className="flex flex-col">
                    <span className="text-xs text-muted-foreground font-medium">
                      Status
                    </span>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(request.status)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div
              className="flex justify-center items-center w-[150px]"
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all flex items-center group/btn"
                onClick={() => {
                  setRegComment("");
                  setDialogAction(null);
                  setShowRejectionDialog(true);
                  setSelectedRegularization(request);
                }}
              >
                <Eye className="w-3.5 h-3.5 mr-2 opacity-60 group-hover/btn:text-[#3194a0] group-hover/btn:opacity-100 transition-all" />
                <span className="group-hover/btn:text-[#3194a0] transition-colors">
                  View
                </span>
                <ChevronRight className="w-3 h-3 ml-1 opacity-0 -translate-x-1 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 group-hover/btn:text-[#3194a0] transition-all" />
              </Button>

              <div className="w-px h-4 bg-slate-200 dark:bg-slate-800 mx-1" />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-6 text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-52 p-1.5 shadow-2xl border-slate-200 dark:border-slate-800"
                >
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-400 font-bold px-2 py-1.5">
                    Request Actions
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {canReviewRequest ? (
                    <>
                      <DropdownMenuItem
                        className="text-emerald-600 cursor-pointer text-xs font-bold focus:text-emerald-700 focus:bg-emerald-50/50 rounded-md py-2.5 px-2.5 mb-0.5 group"
                        onClick={() => {
                          if (!canEdit("regularization_requests", true)) return;
                          setRegComment("");
                          setShowRejectionDialog(true);
                          setSelectedRegularization(request);
                          setDialogAction("accept");
                        }}
                      >
                        <div className="w-7 h-7 rounded-full bg-emerald-50 group-focus:bg-emerald-100 flex items-center justify-center mr-3 transition-colors">
                          <CheckCircle2 className="w-4 h-4" />
                        </div>
                        Approve Request
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-rose-600 cursor-pointer text-xs font-bold focus:text-rose-700 focus:bg-rose-50/50 rounded-md py-2.5 px-2.5 group"
                        onClick={() => {
                          if (!canEdit("regularization_requests", true)) return;
                          setRegComment("");
                          setShowRejectionDialog(true);
                          setSelectedRegularization(request);
                          setDialogAction("reject");
                        }}
                      >
                        <div className="w-7 h-7 rounded-full bg-rose-50 group-focus:bg-rose-100 flex items-center justify-center mr-3 transition-colors">
                          <XCircle className="w-4 h-4" />
                        </div>
                        Reject Request
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <div className="px-2 py-3 flex items-center justify-center bg-slate-50/50 dark:bg-slate-800/30 rounded-md border border-dashed border-slate-200 dark:border-slate-700 my-1">
                      <span className="text-[11px] text-slate-400 font-medium italic flex items-center gap-1.5">
                        <Info className="w-3 h-3" /> No pending actions
                      </span>
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      );
    });
  };

  if (loading && regularizationRequests.length === 0 && earlyPunchOutRequests.length === 0) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-60" />
          <Skeleton className="h-4 w-80" />
        </div>
        <Skeleton className="h-10 w-72" />
        <Card>
          <CardContent className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Regularization Requests
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage attendance correction requests
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Requests
            </CardTitle>
            <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">
              {statusRequests.length}
            </div>
          </CardContent>
        </Card>
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending
            </CardTitle>
            <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">
              {statusRequests.filter((r) => r.status === "pending").length}
            </div>
          </CardContent>
        </Card>
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Approved
            </CardTitle>
            <CheckCircle className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">
              {statusRequests.filter((r) => r.status === "approved").length}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full space-y-4"
      >
        <TabsList className="flex justify-start space-x-8 overflow-x-auto bg-transparent h-auto p-0 no-scrollbar">
          <TabsTrigger value="late-arrival">Late Arrival</TabsTrigger>
          <TabsTrigger value="missed-punch-in">Missed Punch-In</TabsTrigger>
          <TabsTrigger value="early-punch-outs">Early Punch Outs</TabsTrigger>
        </TabsList>

        <TabsContent value="late-arrival" className="mt-0">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Late Arrival Requests</CardTitle>
                  <CardDescription>
                    Review and manage regularization requests for late arrivals
                  </CardDescription>
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {renderRequestList(
                  pagedRequests,
                  "No requests found",
                  "We couldn't find any requests matching your current filters. Try adjusting them or checking back later.",
                  "late-arrival",
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="missed-punch-in" className="mt-0">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Missed Punch-In Requests</CardTitle>
                  <CardDescription>
                    Review requests from employees who forgot to punch in
                  </CardDescription>
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {renderRequestList(
                  pagedRequests,
                  "No missed punch-in requests yet",
                  "Requests from employees who forgot to punch in will appear here.",
                  "missed-punch-in",
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="early-punch-outs" className="mt-0">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Early Punch Out Requests</CardTitle>
                  <CardDescription>
                    Review and manage regularization requests for early punch
                    outs
                  </CardDescription>
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {renderRequestList(
                  pagedRequests,
                  "No early punch out requests yet",
                  "Once early punch out requests are available, they will be listed in this tab.",
                  "early-punch-out",
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <RequestDetailsDialog
        open={showRejectionDialog}
        onOpenChange={setShowRejectionDialog}
        title={
          <>
            {selectedRegularization && isEarlyPunchOutRequest && (
              <>
                <Eye className="h-6 w-6 text-blue-500" />
                View Early Punch Out Details
              </>
            )}
            {selectedRegularization &&
              !isEarlyPunchOutRequest &&
              isPendingRequest && (
                <>
                  {dialogAction === "reject" ? (
                    <XCircle className="h-6 w-6 text-red-500" />
                  ) : (
                    <CheckCircle className="h-6 w-6 text-green-500" />
                  )}
                  {dialogAction === "reject" ? "Reject" : "Accept"} Request
                </>
              )}
            {selectedRegularization &&
              !isEarlyPunchOutRequest &&
              !isPendingRequest && (
                <>
                  <Eye className="h-6 w-6 text-blue-500" />
                  View Regularization Details
                </>
              )}
          </>
        }
        description={
          selectedRegularization && isEarlyPunchOutRequest
            ? "Review the details of this early punch out record."
            : selectedRegularization && canReviewSelectedRequest
              ? dialogAction === "reject"
                ? "Provide a reason for rejection. This is mandatory."
                : "Confirm the approval for the selected regularization request."
              : "Review the details of this already processed request."
        }
        fields={
          selectedRegularization
            ? [
                {
                  label: "Employee",
                  value: `${selectedRegularization.employee_name} (${selectedRegularization.employee_id})`,
                  valueClassName: "font-semibold text-sm",
                },
                selectedRegularization.status && {
                    label: "Status",
                    value: selectedRegularization.status.toUpperCase(),
                    valueClassName: `font-semibold text-sm ${
                      selectedRegularization.status === "approved"
                        ? "text-green-600"
                        : selectedRegularization.status === "rejected"
                          ? "text-red-600"
                          : "text-yellow-600"
                    }`,
                  },
                {
                  label: "Date",
                  value: new Date(
                    selectedRegularization.date,
                  ).toLocaleDateString("en-IN"),
                },
                ...(isEarlyPunchOutRequest
                  ? [
                      {
                        label: "Expected Check Out",
                        value:
                          selectedRegularization.expected_check_out || "N/A",
                      },
                      {
                        label: "Actual Check Out",
                        value: selectedRegularization.actual_check_out || "N/A",
                      },
                      {
                        label: "Early Minutes",
                        value: selectedRegularization.early_minutes ?? "N/A",
                      },
                      {
                        label: "Impacts Half Day",
                        value: selectedRegularization.impacts_half_day
                          ? "Yes"
                          : "No",
                      },
                      {
                        label: "Requested At",
                        value: selectedRegularization.requested_at
                          ? new Date(
                              selectedRegularization.requested_at,
                            ).toLocaleString("en-IN")
                          : "N/A",
                      },
                      {
                        label: "Reason",
                        value: selectedRegularization.reason || "N/A",
                        fullWidth: true,
                        valueClassName: "text-sm italic text-foreground/80",
                      },
                      {
                        label: "Remarks",
                        value: selectedRegularization.remarks || "N/A",
                        fullWidth: true,
                        valueClassName: "text-sm italic text-foreground/80",
                      },
                      {
                        label: "Review Comments",
                        value: selectedRegularization.review_comments || "N/A",
                        fullWidth: true,
                        valueClassName: "text-sm italic text-foreground/80",
                      },
                      selectedRegularization.attachment_signed_url && {
                        label: "Attachment",
                        fullWidth: true,
                        render: () => (
                          <a
                            href={selectedRegularization.attachment_signed_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                          >
                            <Eye className="h-4 w-4" /> View attachment
                          </a>
                        ),
                      },
                    ]
                  : [
                      ...(selectedRegularization.event_type ===
                      "missed_punch_in"
                        ? [
                            {
                              label: "Punch In (Submitted)",
                              value:
                                selectedRegularization.actual_check_in || "N/A",
                            },
                            {
                              label: "Punch Out (Submitted)",
                              value:
                                selectedRegularization.actual_check_out || "N/A",
                            },
                          ]
                        : []),
                      {
                        label: "Reason",
                        value: selectedRegularization.remarks,
                        fullWidth: true,
                        valueClassName: "text-sm italic text-foreground/80",
                      },
                      {
                        label: "Review Comments",
                        value: selectedRegularization.review_comments || "N/A",
                        fullWidth: true,
                        valueClassName: "text-sm italic text-foreground/80",
                      },
                      selectedRegularization.attachment_signed_url && {
                        label: "Attachment",
                        fullWidth: true,
                        render: () => (
                          <a
                            href={selectedRegularization.attachment_signed_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                          >
                            <Eye className="h-4 w-4" /> View attachment
                          </a>
                        ),
                      },
                    ]),
                selectedRegularization.reviewed_by_name && {
                    label: "Reviewed By",
                    value: `${selectedRegularization.reviewed_by_name} on ${new Date(
                      selectedRegularization.reviewed_at,
                    ).toLocaleDateString("en-IN")}`,
                    fullWidth: true,
                  },
              ]
            : []
        }
        footer={
          <>
            {/* 💡 Logic: Always show Cancel/Close button */}
            <Button
              variant="outline"
              onClick={() => setShowRejectionDialog(false)}
            >
              {selectedRegularization && canReviewSelectedRequest
                ? "Cancel"
                : "Close"}
            </Button>

            {/* 💡 Logic: Only show Accept/Reject buttons if status is PENDING */}
            {selectedRegularization &&
              canReviewSelectedRequest &&
              dialogAction === "reject" && (
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (!canEdit("regularization_requests", true)) return;
                    // Check if required field (comment) is missing for rejection
                    if (dialogAction === "reject" && !regComment.trim()) {
                      toast.error("Rejection requires a comment/reason.");
                      return;
                    }
                    upsertRegularizationRequest(
                      selectedRegularization,
                      "rejected",
                    );
                  }}
                  className="w-full sm:w-auto flex items-center space-x-2"
                >
                  <XCircle className="h-4 w-4" />
                  <span>Reject Request</span>
                </Button>
              )}

            {selectedRegularization &&
              canReviewSelectedRequest &&
              dialogAction === "accept" && (
                <Button
                  onClick={() => {
                    if (!canEdit("regularization_requests", true)) return;
                    upsertRegularizationRequest(selectedRegularization, "accept");
                  }}
                  className="w-full sm:w-auto flex items-center space-x-2 bg-primary/90 hover:bg-primary/70"
                >
                  <CheckCircle className="h-4 w-4" />
                  <span>Accept Request</span>
                </Button>
              )}
          </>
        }
      >
        {/* === Admin Comment/Remark Input (Only editable when pending) === */}
        {selectedRegularization && canReviewSelectedRequest && (
          <div>
            <Label className="text-sm font-medium flex justify-between">
              Admin Comment
              <span className="text-xs text-green-500">
                {dialogAction === "reject" ? "(Mandatory)" : "(Optional)"}
              </span>
            </Label>
            <Textarea
              rows={3}
              value={regComment}
              placeholder={
                dialogAction === "reject"
                  ? "Reason for rejection..."
                  : "Add optional comments for approval..."
              }
              onChange={(e) => setRegComment(e.target.value)}
              required={dialogAction === "reject"}
            />
          </div>
        )}
      </RequestDetailsDialog>

      {filteredRequests.length > 0 && (
        <div className="flex items-center justify-end sm:justify-between gap-3 mt-4">
          <PaginationPageSize value={pageSize} onValueChange={handlePageSizeChange} />
          <Pagination className="mx-0 justify-end">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  aria-disabled={page === 1}
                  className={page === 1 ? "pointer-events-none opacity-50" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    setPage((p) => Math.max(1, p - 1));
                  }}
                />
              </PaginationItem>

              {buildPages(page, totalPages).map((p, idx) => {
                if (p === "...") {
                  return (
                    <PaginationItem key={`e-${idx}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                }

                return (
                  <PaginationItem key={p}>
                    <PaginationLink
                      href="#"
                      isActive={p === page}
                      onClick={(e) => {
                        e.preventDefault();
                        setPage(p);
                      }}
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  aria-disabled={page === totalPages}
                  className={
                    page === totalPages ? "pointer-events-none opacity-50" : ""
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    setPage((p) => Math.min(totalPages, p + 1));
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default Regularization;
